import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  className?: string;
}

export const Stepper: React.FC<StepperProps> = ({ 
  steps, 
  currentStep, 
  className = '' 
}) => {
  return (
    <nav className={`mb-8 ${className}`} aria-label="Progress">
      <ol className="flex items-center justify-between">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const isUpcoming = stepNumber > currentStep;

          return (
            <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'flex-1' : ''}`}>
              {/* Connector line */}
              {index !== steps.length - 1 && (
                <div className="absolute top-5 left-0 -ml-px mt-0.5 h-0.5 w-full flex">
                  <div className={`h-0.5 flex-auto ${
                    isCompleted ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                </div>
              )}

              <div className="relative flex items-start group">
                {/* Step indicator */}
                <div className="flex-shrink-0">
                  <div className={`
                    relative w-10 h-10 flex items-center justify-center rounded-full border-2 transition-colors
                    ${isCompleted 
                      ? 'bg-primary-600 border-primary-600 text-white' 
                      : isCurrent 
                        ? 'border-primary-600 bg-white dark:bg-gray-900 text-primary-600' 
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-500'
                    }
                  `}>
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : step.icon ? (
                      <div className="w-5 h-5 flex items-center justify-center">
                        {step.icon}
                      </div>
                    ) : (
                      <span className="text-sm font-medium">{stepNumber}</span>
                    )}
                  </div>
                </div>

                {/* Step content */}
                <div className="ml-4 min-w-0 flex flex-col">
                  <span className={`text-sm font-medium transition-colors ${
                    isCurrent 
                      ? 'text-primary-600 dark:text-primary-400' 
                      : isCompleted 
                        ? 'text-gray-900 dark:text-gray-100' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                  {step.description && (
                    <span className={`text-xs mt-0.5 ${
                      isCurrent 
                        ? 'text-primary-500 dark:text-primary-300' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {step.description}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}; 